import React from 'react';

const AuthLayout = ({ children }) => {
  return (
    <div className="fixed inset-0 flex overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
      {/* Left Side - Landing Page */}
      <div className="w-1/2 bg-gradient-to-br from-teal-600 to-teal-800 text-white flex flex-col justify-start items-center p-12 shadow-2xl">
        <div className="max-w-md text-center">
          {/* Advanced UrbanSense Logo */}
          <div className="flex justify-center mb-8">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200" className="h-auto text-white w-36">
              <defs>
                <linearGradient id="skyline-gradient" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" style={{stopColor: 'currentColor', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: 'currentColor', stopOpacity: 0.6}} />
                </linearGradient>
                <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{stopColor: 'currentColor', stopOpacity: 0.8}} />
                  <stop offset="50%" style={{stopColor: 'currentColor', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: 'currentColor', stopOpacity: 0.8}} />
                </linearGradient>
              </defs>
              
              {/* Skyline */}
              <g fill="url(#skyline-gradient)">
                <path d="M10 120h30v80H10z" />
                <path d="M50 90h30v110H50z" />
                <path d="M90 70h30v130H90z" />
                <path d="M130 100h30v100H130z" />
                <path d="M170 50h30v150H170z" />
                <path d="M210 80h30v120H210z" />
                <path d="M250 110h30v90H250z" />
              </g>
              
              {/* Windows */}
              <g fill="currentColor" fillOpacity="0.3">
                <rect x="15" y="130" width="20" height="5" />
                <rect x="15" y="140" width="20" height="5" />
                <rect x="15" y="150" width="20" height="5" />
                <rect x="55" y="100" width="20" height="5" />
                <rect x="55" y="110" width="20" height="5" />
                <rect x="55" y="120" width="20" height="5" />
                <rect x="95" y="80" width="20" height="5" />
                <rect x="95" y="90" width="20" height="5" />
                <rect x="95" y="100" width="20" height="5" />
                <rect x="135" y="110" width="20" height="5" />
                <rect x="135" y="120" width="20" height="5" />
                <rect x="135" y="130" width="20" height="5" />
                <rect x="175" y="60" width="20" height="5" />
                <rect x="175" y="70" width="20" height="5" />
                <rect x="175" y="80" width="20" height="5" />
                <rect x="215" y="90" width="20" height="5" />
                <rect x="215" y="100" width="20" height="5" />
                <rect x="215" y="110" width="20" height="5" />
                <rect x="255" y="120" width="20" height="5" />
                <rect x="255" y="130" width="20" height="5" />
                <rect x="255" y="140" width="20" height="5" />
              </g>
              
              {/* Data Waves */}
              <path d="M0 160 C50 140, 100 180, 150 160 S250 140, 300 160 L300 200 L0 200 Z" fill="url(#wave-gradient)" />
              <path d="M0 170 C50 150, 100 190, 150 170 S250 150, 300 170" fill="none" stroke="currentColor" strokeWidth="2" strokeOpacity="0.5" />
              <path d="M0 180 C50 160, 100 200, 150 180 S250 160, 300 180" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" />
            </svg>
          </div>
          <h1 className="text-6xl font-bold mb-4 text-shadow">UrbanSense</h1>
          <p className="text-2xl mb-8 opacity-90">
            Empowering Smart Cities with Intelligent Sensing
          </p>
          <ul className="text-left text-lg mb-8 bg-teal-700 bg-opacity-50 p-6 rounded-lg shadow-inner">
            <li className="mb-4 flex items-center">
              <svg className="w-6 h-6 mr-3 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
              Advanced IoT sensor networks
            </li>
            <li className="mb-4 flex items-center">
              <svg className="w-6 h-6 mr-3 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
              Real-time environmental monitoring
            </li>
            <li className="mb-4 flex items-center">
              <svg className="w-6 h-6 mr-3 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
              Predictive urban analytics
            </li>
            <li className="mb-4 flex items-center">
              <svg className="w-6 h-6 mr-3 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
              Community-driven insights
            </li>
          </ul>
          <p className="text-xl mb-8 font-semibold">
            Shape the future of your city with UrbanSense
          </p>
    {/* Embedded Video */}
    <div className="mt-8">
              <iframe 
                src="https://player.vimeo.com/video/1001031354?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" 
                width="100%" 
                height="315" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen 
                className="rounded-lg shadow-lg"
                title="UrbanSense Video"
              ></iframe>
            </div>
 
        </div>
      </div>
      
      {/* Right Side - Form Content */}
      <div className="w-1/2 bg-white flex justify-center items-center p-12 overflow-y-auto">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg border border-teal-100">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;